// ── Dashboard — 매장 운영 한눈에 보기 ─────────────────────────
// 오늘의 매출·인건비·재고 알림 + 이번달 손익 + 빠른 액션
import React, { useMemo, useState } from 'react';
import { useIsMobile } from '../useIsMobile';
import { useTransactions } from '../useTransactions';
import { analyzeShift } from '../utils/payroll';
import { calcAvgUsage, mergeUsage, countNeedOrder } from '../utils/inventoryAlerts';
import { calcRecipeBasedUsage } from '../utils/recipeUsage';
import { calcStoreHealthScore } from '../utils/storeHealth';
import StoreHealthScore from './StoreHealthScore';
import MenuProfitabilityRanking from './MenuProfitabilityRanking';
import MenuEngineeringMatrix from './MenuEngineeringMatrix';
import AbcAnalysisCard from './AbcAnalysisCard';
import { calcMenuProfitability, aggregateMenuSales, enrichWithActualSales } from '../utils/menuProfitability';
import DashboardPeriodBar, { getInitialPeriod, periodToKey } from './DashboardPeriodBar';
import RevenueCalendar from './RevenueCalendar';
import GoalsManager from './GoalsManager';
import InsightsCard from './InsightsCard';
import { generateInsights } from '../utils/insights';
import { loadGoals } from '../utils/goals';
import QuickRevenueEntry from './QuickRevenueEntry';
import MonthlyProgressChart from './MonthlyProgressChart';
import QuickExpenseEntry from './QuickExpenseEntry';
import QuickEntryFAB from './QuickEntryFAB';
import GroupBuyTeaser from './GroupBuyTeaser';
import OrderRecommendCard from './OrderRecommendCard';
import IndustryBenchmark from './IndustryBenchmark';
import PeerComparePreview from './PeerComparePreview';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');
const fmtWon = n => fmt(n) + '원';

const KOR_DAYS = ['일', '월', '화', '수', '목', '금', '토'];

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getPrevMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function Dashboard({
  user,
  profitLoss = {},
  orderHistory = [],
  prices = [],
  inventory = {},
  categories = [],
  employees = [],
  schedules = {},
  payrollSettings = {},
  onNavigate,  // (tabId) => void
}) {
  const isMobile = useIsMobile();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const todayDayOfWeek = KOR_DAYS[today.getDay()];

  // ── 기간 상태 (월/분기/연 토글) ──
  const [period, setPeriod] = useState(getInitialPeriod);
  // 현재는 전부 월 단위로 매핑 (분기/연은 첫 달로 집계)
  const monthKey = useMemo(() => {
    if (period.type === 'month')   return `${period.year}-${String(period.month).padStart(2, '0')}`;
    if (period.type === 'quarter') return `${period.year}-${String((period.quarter - 1) * 3 + 1).padStart(2, '0')}`;
    return `${period.year}-01`;
  }, [period]);
  const prevMonthKey = useMemo(() => {
    const [y, m] = monthKey.split('-').map(Number);
    let pm = m - 1, py = y;
    if (pm < 1) { pm = 12; py--; }
    return `${py}-${String(pm).padStart(2, '0')}`;
  }, [monthKey]);

  // ── 거래장부 transactions — monthKey 기반 12개월 lazy 쿼리 (5년+ 매장 대응) ──
  const txDateRange = useMemo(() => {
    const start = new Date(monthKey + '-01');
    start.setMonth(start.getMonth() - 11);
    const startDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`;
    const endDate = `${monthKey}-31`;
    return { startDate, endDate };
  }, [monthKey]);
  const { transactions, addTransaction } = useTransactions(user, {
    startDate: txDateRange.startDate,
    endDate: txDateRange.endDate,
    limitCount: 5000,
  });

  // ── 빠른 매출 입력 모달 ──
  const [quickEntryOpen, setQuickEntryOpen] = useState(false);
  const [quickEntryDate, setQuickEntryDate] = useState(null);
  const handleQuickEntrySave = async (txs, options = {}) => {
    // 같은 날짜 기존 매출 거래 삭제 후 새로 저장 (중복 방지)
    const replaceIds = options.replaceIds || [];
    for (const id of replaceIds) {
      try { await deleteTransaction(id); }
      catch (e) { console.warn('replace delete failed:', e.message); }
    }
    for (const tx of txs) {
      await addTransaction(tx);
    }
  };

  // ── 빠른 비용 입력 모달 ──
  const [quickExpenseOpen, setQuickExpenseOpen] = useState(false);
  const handleQuickExpenseSave = async (txs) => {
    for (const tx of txs) {
      await addTransaction(tx);
    }
  };

  // ── 거래장부 매출을 record.revenue.ledger에 주입한 augmented profitLoss ──
  // 모든 위젯이 동일한 매출 정의(수기 + 거래장부)를 보도록 통일
  const augmentedProfitLoss = useMemo(() => {
    // 카테고리별 자동집계 (월별)
    const monthlyRevByMethod = {};   // 'YYYY-MM' → { card, cash, delivery }
    const monthlyCostByCategory = {}; // 'YYYY-MM' → { ingredients, labor, rent, utilities, other }
    const bucketOf = (cat) => {
      const major = String(cat?.major || '').toLowerCase();
      const sub = String(cat?.sub || '').toLowerCase();
      if (major === 'material') return 'ingredients';
      if (major === 'labor') return 'labor';
      if (major === 'operation') {
        if (sub === 'rent') return 'rent';
        if (sub === 'utilities' || sub === 'communication') return 'utilities';
        return 'other';
      }
      return 'other';
    };
    const methodOf = (t) => {
      // category.sub 우선, 없으면 revenueCategory (POS 임포트는 revenueCategory에 결제수단 저장)
      const sub = String(t?.category?.sub || t?.revenueCategory || '').toLowerCase();
      if (sub === 'sales_card' || sub === 'card') return 'card';
      if (sub === 'sales_cash' || sub === 'cash') return 'cash';
      if (sub === 'sales_delivery' || sub === 'delivery') return 'delivery';
      // 미분류 매출은 cash로 합산
      return 'cash';
    };
    for (const t of (transactions || [])) {
      const m = (t?.date || '').slice(0, 7);
      if (!m) continue;
      const amount = (Number(t.supply) || 0) + (Number(t.vat) || 0);
      if (t?.type === 'income') {
        if (!monthlyRevByMethod[m]) monthlyRevByMethod[m] = { card: 0, cash: 0, delivery: 0 };
        monthlyRevByMethod[m][methodOf(t)] += amount;
      } else if (t?.type === 'expense') {
        if (!monthlyCostByCategory[m]) monthlyCostByCategory[m] = { ingredients: 0, labor: 0, rent: 0, utilities: 0, other: 0 };
        monthlyCostByCategory[m][bucketOf(t.category)] += amount;
      }
    }
    // 수기 > 0이면 수기, 아니면 거래장부 자동집계 (이중합산 X)
    const out = { ...(profitLoss || {}) };
    const allMonths = new Set([...Object.keys(monthlyRevByMethod), ...Object.keys(monthlyCostByCategory)]);
    for (const m of allMonths) {
      const cur = out[m] || {};
      const rev = cur.revenue || {};
      const costs = cur.costs || {};
      const rl = monthlyRevByMethod[m] || { card: 0, cash: 0, delivery: 0 };
      const cl = monthlyCostByCategory[m] || { ingredients: 0, labor: 0, rent: 0, utilities: 0, other: 0 };
      out[m] = {
        ...cur,
        revenue: {
          ...rev,
          card:     (Number(rev.card) > 0)     ? Number(rev.card)     : rl.card,
          cash:     (Number(rev.cash) > 0)     ? Number(rev.cash)     : rl.cash,
          delivery: (Number(rev.delivery) > 0) ? Number(rev.delivery) : rl.delivery,
        },
        costs: {
          ...costs,
          ingredients: (Number(costs.ingredients) > 0) ? Number(costs.ingredients) : cl.ingredients,
          labor:       (Number(costs.labor) > 0)       ? Number(costs.labor)       : cl.labor,
          rent:        (Number(costs.rent) > 0)        ? Number(costs.rent)        : cl.rent,
          utilities:   (Number(costs.utilities) > 0)   ? Number(costs.utilities)   : cl.utilities,
          other:       (Number(costs.other) > 0)       ? Number(costs.other)       : cl.other,
        },
      };
    }
    return out;
  }, [profitLoss, transactions]);

  // ── 이번달 손익 요약 ──
  const monthSummary = useMemo(() => {
    const rec = augmentedProfitLoss?.[monthKey] || {};
    const rev = rec.revenue || {};
    const costs = rec.costs || {};
    const totalRevenue = (Number(rev.card) || 0) + (Number(rev.cash) || 0) + (Number(rev.delivery) || 0);
    const totalCosts = (Number(costs.ingredients) || 0) + (Number(costs.labor) || 0)
      + (Number(costs.rent) || 0) + (Number(costs.utilities) || 0) + (Number(costs.other) || 0);
    const profit = totalRevenue - totalCosts;
    const margin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : null;
    return { totalRevenue, totalCosts, profit, margin, costs };
  }, [augmentedProfitLoss, monthKey]);

  // ── 지난달 손익 (비교용) ──
  const prevSummary = useMemo(() => {
    const rec = augmentedProfitLoss?.[prevMonthKey] || {};
    const rev = rec.revenue || {};
    const costs = rec.costs || {};
    const totalRevenue = (Number(rev.card) || 0) + (Number(rev.cash) || 0) + (Number(rev.delivery) || 0);
    const totalCosts = (Number(costs.ingredients) || 0) + (Number(costs.labor) || 0)
      + (Number(costs.rent) || 0) + (Number(costs.utilities) || 0) + (Number(costs.other) || 0);
    return { totalRevenue, totalCosts, profit: totalRevenue - totalCosts };
  }, [augmentedProfitLoss, prevMonthKey]);

  // ── 오늘 출근 직원 + 오늘 인건비 ──
  const todayPayroll = useMemo(() => {
    const dayShifts = schedules?.[monthKey]?.[today.getDate()] || {};
    const workingEmps = [];
    let todayLaborCost = 0;
    for (const emp of employees.filter(e => e.active !== false)) {
      const sh = dayShifts[emp.id];
      if (!sh) continue;
      const analysis = analyzeShift(sh, payrollSettings);
      if (!analysis || analysis.totalHours <= 0) continue;
      const wage = Number(emp.hourlyWage) || 0;
      const cost = Math.round(analysis.totalHours * wage);
      workingEmps.push({
        id: emp.id, name: emp.name, hours: analysis.totalHours,
        cost, blocks: sh.blocks || [],
      });
      todayLaborCost += cost;
    }
    return { workingEmps, todayLaborCost };
  }, [schedules, monthKey, today.getDate(), employees, payrollSettings]);

  // ── 매장 건강 점수 (100점) — 동 기간 매출 비교 위해 transactions 전달 ──
  const healthScore = useMemo(() => calcStoreHealthScore({
    profitLoss: augmentedProfitLoss, monthKey, prevMonthKey,
    prices, inventory, orderHistory, categories, payrollSettings,
    transactions,
  }), [augmentedProfitLoss, monthKey, prevMonthKey, prices, inventory, orderHistory, categories, payrollSettings, transactions]);

  // ── 발주 알림 카운트 ──
  const inventoryAlertCount = useMemo(() => {
    if (!prices || prices.length === 0) return 0;
    const orderUsage = calcAvgUsage(orderHistory, 3);
    const recipeUsage = calcRecipeBasedUsage(categories, prices, payrollSettings?.expectedMonthlyServings);
    const merged = mergeUsage(orderUsage, recipeUsage);
    return countNeedOrder(prices, inventory, orderHistory, merged);
  }, [prices, inventory, orderHistory, categories, payrollSettings?.expectedMonthlyServings]);

  // ── 메뉴 엔지니어링 + ABC 분석용 enriched menus ──
  const enrichedMenus = useMemo(() => {
    const base = calcMenuProfitability({ categories: categories || [], prices: prices || [] });
    const dateRange = { start: `${monthKey}-01`, end: `${monthKey}-31` };
    const sales = aggregateMenuSales(transactions || [], dateRange);
    return enrichWithActualSales(base, sales);
  }, [categories, prices, transactions, monthKey]);

  // ── 자동 인사이트 (룰 기반) — 동 기간 비교 위해 transactions 전달 ──
  const insights = useMemo(() => generateInsights({
    profitLoss: augmentedProfitLoss, monthKey, prevMonthKey,
    prices, inventory, orderHistory, categories,
    inventoryAlertCount, goals: loadGoals(),
    transactions,
  }), [augmentedProfitLoss, monthKey, prevMonthKey, prices, inventory, orderHistory, categories, inventoryAlertCount, transactions]);

  // ── 6개월 매출 트렌드 ──
  const trend = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const rec = augmentedProfitLoss?.[k] || {};
      const rev = rec.revenue || {};
      const costs = rec.costs || {};
      const r = (Number(rev.card) || 0) + (Number(rev.cash) || 0) + (Number(rev.delivery) || 0);
      const c = (Number(costs.ingredients) || 0) + (Number(costs.labor) || 0)
        + (Number(costs.rent) || 0) + (Number(costs.utilities) || 0) + (Number(costs.other) || 0);
      months.push({ month: k, label: k.slice(5) + '월', revenue: r, costs: c, profit: r - c });
    }
    return months;
  }, [augmentedProfitLoss, today.getMonth()]);

  // ── 매출 변화율 ──
  const revenueDelta = prevSummary.totalRevenue > 0
    ? ((monthSummary.totalRevenue - prevSummary.totalRevenue) / prevSummary.totalRevenue) * 100
    : null;

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#FAFAFA' }}>
      <div style={{ padding: isMobile ? '16px' : '28px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* ── 인사 헤더 (V59) ── */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: isMobile ? '13px' : '14px', color: '#71717A', marginBottom: 4, fontWeight: 500 }}>
            {today.getFullYear()}년 {today.getMonth() + 1}월 {today.getDate()}일 ({todayDayOfWeek}요일)
          </div>
          <h1 style={{
            fontSize: isMobile ? '24px' : '32px', fontWeight: 800, color: '#18181B',
            margin: 0, letterSpacing: '-0.02em', lineHeight: 1.2,
          }}>
            안녕하세요, <span style={{ color: '#18181B' }}>{user?.displayName || user?.email?.split('@')[0] || '사장'}님</span>
          </h1>
          <p style={{ fontSize: isMobile ? '13px' : '15px', color: '#52525B', marginTop: 8, fontWeight: 500 }}>
            오늘도 매장 운영 상황 한눈에 확인하세요
          </p>
        </div>

        {/* ── 기간 선택 바 (sticky) ── */}
        <DashboardPeriodBar period={period} onChange={setPeriod} isMobile={isMobile} />

        {/* ── 자동 인사이트 (Top 우선순위) ── */}
        <InsightsCard insights={insights} onNavigate={onNavigate} isMobile={isMobile} />

        {/* ── 매장 건강 점수 (100점) ── */}
        <StoreHealthScore score={healthScore} isMobile={isMobile} onNavigate={onNavigate} />

        {/* ── 이번달 4대 지표 ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: '10px', marginBottom: '18px',
        }}>
          <BigCard
            label="이번달 매출"
            value={fmtWon(monthSummary.totalRevenue)}
            sub={revenueDelta !== null
              ? `지난달 대비 ${revenueDelta >= 0 ? '+' : ''}${revenueDelta.toFixed(1)}%`
              : '지난달 데이터 없음'}
            icon="💰"
            color="#FF6F0F"
            highlighted
          />
          <BigCard
            label="이번달 인건비"
            value={fmtWon(monthSummary.costs?.labor || 0)}
            sub={`총 비용의 ${monthSummary.totalCosts > 0 ? Math.round(((monthSummary.costs?.labor || 0) / monthSummary.totalCosts) * 100) : 0}%`}
            icon="💼"
            color="#1A56A0"
          />
          <BigCard
            label="이번달 순이익"
            value={fmtWon(monthSummary.profit)}
            sub={prevSummary.profit !== 0
              ? `지난달 ${fmtWon(prevSummary.profit)}`
              : '지난달 -'}
            icon={monthSummary.profit >= 0 ? '📈' : '📉'}
            color={monthSummary.profit >= 0 ? '#FF6F0F' : '#52525B'}
          />
          <BigCard
            label="이번달 마진율"
            value={monthSummary.margin !== null ? `${monthSummary.margin.toFixed(1)}%` : '-'}
            sub={monthSummary.margin === null ? '매출 입력 필요'
              : monthSummary.margin >= 30 ? '✅ 양호'
              : monthSummary.margin >= 10 ? '⚠️ 평균'
              : monthSummary.margin >= 0 ? '🔴 낮음'
              : '🚨 손실'}
            icon="🎯"
            color={
              monthSummary.margin === null ? '#9ca3af'
              : monthSummary.margin >= 30 ? '#FF6F0F'
              : monthSummary.margin >= 10 ? '#EA580C'
              : '#52525B'
            }
          />
        </div>

        {/* ── 메뉴 이익률 랭킹 ── */}
        <MenuProfitabilityRanking
          categories={categories}
          prices={prices}
          transactions={transactions || []}
          monthKey={monthKey}
          isMobile={isMobile}
          onNavigate={onNavigate}
        />

        {/* ── 메뉴 엔지니어링 4분면 (AF) ── */}
        <MenuEngineeringMatrix menus={enrichedMenus} isMobile={isMobile} />

        {/* ── ABC 분석 Pareto (AF) ── */}
        <AbcAnalysisCard menus={enrichedMenus} isMobile={isMobile} />

        {/* ── 외식업 벤치마크 (평균 vs 본인) ── */}
        <IndustryBenchmark
          profitLoss={augmentedProfitLoss}
          monthKey={monthKey}
          prevMonthKey={prevMonthKey}
          isMobile={isMobile}
        />

        {/* ── 매장 목표 관리 ── */}
        <GoalsManager
          profitLoss={augmentedProfitLoss}
          monthKey={monthKey}
          isMobile={isMobile}
        />

        {/* ── 이달 매출 캘린더 ── */}
        <RevenueCalendar
          transactions={transactions || []}
          monthKey={monthKey}
          isMobile={isMobile}
          onClickDay={(dateStr) => { setQuickEntryDate(dateStr); setQuickEntryOpen(true); }}
          onAddRevenue={() => { setQuickEntryDate(null); setQuickEntryOpen(true); }}
        />

        {/* ── 이달 진행 차트 (누적 + 목표 트랙) ── */}
        <MonthlyProgressChart
          transactions={transactions || []}
          monthKey={monthKey}
          isMobile={isMobile}
        />

        {/* ── 발주 추천 (재고+사용속도 기반 Top 5) ── */}
        <OrderRecommendCard
          prices={prices}
          inventory={inventory}
          orderHistory={orderHistory}
          categories={categories}
          onNavigate={onNavigate}
          isMobile={isMobile}
        />

        {/* ── 공동구매 (실시간 Firestore 카드 + 옵트인 알림) ── */}
        <GroupBuyTeaser isMobile={isMobile} onNavigate={onNavigate} />

        {/* ── 동네·업종 셈핏 매장 비교 (V79: data-for-data 회로) ── */}
        <PeerComparePreview
          transactions={transactions || []}
          categories={categories || []}
          prices={prices || []}
          employees={employees || []}
          payrollSettings={payrollSettings || {}}
          isMobile={isMobile}
          onNavigate={onNavigate}
        />

        {/* ── 빠른 매출 입력 모달 ── */}
        <QuickRevenueEntry
          isOpen={quickEntryOpen}
          defaultDate={quickEntryDate}
          categories={categories}
          transactions={transactions || []}
          isMobile={isMobile}
          onClose={() => setQuickEntryOpen(false)}
          onSave={handleQuickEntrySave}
        />

        {/* ── 빠른 비용 입력 모달 ── */}
        <QuickExpenseEntry
          isOpen={quickExpenseOpen}
          defaultDate={null}
          isMobile={isMobile}
          onClose={() => setQuickExpenseOpen(false)}
          onSave={handleQuickExpenseSave}
        />

        {/* ── FAB (Floating Action Button) ── */}
        <QuickEntryFAB
          isMobile={isMobile}
          onOpenRevenue={() => { setQuickEntryDate(null); setQuickEntryOpen(true); }}
          onOpenExpense={() => setQuickExpenseOpen(true)}
        />

        {/* ── 오늘 운영 상황 ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '18px' }}>

          {/* 오늘 출근 직원 */}
          <SectionCard
            icon="👥"
            title="오늘 출근 직원"
            color="#1A56A0"
            onClick={() => onNavigate?.('payroll')}
            footer={`예상 인건비 ${fmtWon(todayPayroll.todayLaborCost)} · 클릭하면 인건비 탭`}
          >
            {todayPayroll.workingEmps.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#bbb', padding: '20px 0', fontSize: 13 }}>
                오늘 출근 예정 직원이 없습니다
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {todayPayroll.workingEmps.map(emp => (
                  <div key={emp.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', background: '#f8fafc', borderRadius: 8,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{emp.name}</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                      {emp.hours}h · {fmtWon(emp.cost)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* 재고·발주 알림 */}
          <SectionCard
            icon="📦"
            title="재고 & 발주 알림"
            color={inventoryAlertCount > 0 ? '#52525B' : '#FF6F0F'}
            onClick={() => onNavigate?.('inventory')}
            footer={inventoryAlertCount > 0
              ? `클릭하면 재고관리 탭 → 발주서로 일괄 추가 가능`
              : '클릭하면 재고관리 탭'}
          >
            {inventoryAlertCount > 0 ? (
              <div style={{
                background: '#F4F4F5', borderRadius: 10, padding: '14px 16px',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 32 }}>🚨</span>
                <div>
                  <div style={{ fontWeight: 800, color: '#52525B', fontSize: 18 }}>
                    {inventoryAlertCount}개 항목 발주 필요
                  </div>
                  <div style={{ fontSize: 12, color: '#52525B', marginTop: 2 }}>
                    안전재고 미만 또는 7일 이내 소진 예상
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                background: '#FAFAFA', borderRadius: 10, padding: '14px 16px',
              }}>
                <div style={{ fontWeight: 800, color: '#18181B', fontSize: 15 }}>
                  재고 상태 양호
                </div>
                <div style={{ fontSize: 12, color: '#52525B', marginTop: 3 }}>
                  {prices.length === 0 ? '단가표에 식재료를 등록하세요' : '발주 필요 항목 없음'}
                </div>
              </div>
            )}
          </SectionCard>
        </div>

        {/* ── 6개월 트렌드 ── */}
        <SectionCard icon="📊" title="6개월 손익 추이" color="#FF6F0F" onClick={() => onNavigate?.('profit')}>
          <TrendChart trend={trend} isMobile={isMobile} />
        </SectionCard>

        {/* ── 빠른 액션 ── */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#374151', marginBottom: 10 }}>
            빠른 액션
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
            gap: 10,
          }}>
            <QuickAction icon="📋" label="레시피"   onClick={() => onNavigate?.('recipes')}  />
            <QuickAction icon="💰" label="단가표"   onClick={() => onNavigate?.('prices')}   />
            <QuickAction icon="📊" label="손익"      onClick={() => onNavigate?.('profit')}   />
            <QuickAction icon="🗃️" label="재고"      onClick={() => onNavigate?.('inventory')}/>
            <QuickAction icon="💼" label="인건비"   onClick={() => onNavigate?.('payroll')}  />
            <QuickAction icon="🏛️" label="세무"      onClick={() => onNavigate?.('tax')}      />
          </div>
        </div>

        {/* ── 데이터 입력 안내 ── */}
        {monthSummary.totalRevenue === 0 && (
          <div style={{
            marginTop: 16, padding: '14px 16px', borderRadius: 12,
            background: '#FAFAFA', border: '1px solid #D4D4D8',
            fontSize: 12, color: '#52525B', lineHeight: 1.7,
          }}>
            💡 <strong>아직 매출 데이터가 없어요</strong> — 손익관리 탭에서 이번달 매출을 입력하면 대시보드가 활성화됩니다.
          </div>
        )}
      </div>
    </div>
  );
}

// ── 4대 지표 카드 ───────────────────────────────────────────
function BigCard({ label, value, sub, icon, color, highlighted }) {
  // V62: 미니멀 — 보더 제거, 화이트 카드만으로 구분. 강조는 글자 크기/색만.
  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      padding: '18px 20px',
      border: 'none',
    }}>
      <div style={{
        fontSize: 12, color: '#71717A',
        marginBottom: 8, fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <span style={{ opacity: 0.6 }}>{icon}</span> {label}
      </div>
      <div style={{
        fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em',
        color: highlighted ? '#FF6F0F' : '#18181B', lineHeight: 1.2,
      }}>{value}</div>
      {sub && (
        <div style={{
          fontSize: 11, marginTop: 6, fontWeight: 500, color: '#71717A',
        }}>{sub}</div>
      )}
    </div>
  );
}

// ── 섹션 카드 (V62 미니멀) ─────────────────────────────────
function SectionCard({ icon, title, color, children, onClick, footer }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 14, overflow: 'hidden',
        border: 'none',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => { if (clickable) e.currentTarget.style.background = '#FAFAFA'; }}
      onMouseLeave={e => { if (clickable) e.currentTarget.style.background = '#fff'; }}
    >
      <div style={{
        padding: '16px 20px',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#18181B', letterSpacing: '-0.01em' }}>{title}</span>
        {clickable && (
          <span style={{ marginLeft: 'auto', fontSize: 14, color: '#A1A1AA' }}>›</span>
        )}
      </div>
      <div style={{ padding: '0 20px 18px' }}>{children}</div>
      {footer && (
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #F4F4F5',
          fontSize: 12, color: '#71717A', fontWeight: 500,
        }}>{footer}</div>
      )}
    </div>
  );
}

// ── 빠른 액션 버튼 ───────────────────────────────────────────
// V32: 이모지 제거 + 깔끔한 텍스트 버튼 (이모지 prop은 backward-compat 위해 유지)
function QuickAction({ icon, label, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '18px 12px',
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
      cursor: 'pointer', transition: 'all 0.15s',
      fontSize: 14, fontWeight: 700, color: '#1f2937',
      letterSpacing: '-0.01em',
    }}
    onMouseEnter={e => {
      e.currentTarget.style.borderColor = '#FF6F0F';
      e.currentTarget.style.background = '#FAFAFA';
      e.currentTarget.style.color = '#FF6F0F';
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(30, 107, 60, 0.08)';
    }}
    onMouseLeave={e => {
      e.currentTarget.style.borderColor = '#e5e7eb';
      e.currentTarget.style.background = '#fff';
      e.currentTarget.style.color = '#1f2937';
      e.currentTarget.style.transform = '';
      e.currentTarget.style.boxShadow = '';
    }}>
      {label}
    </button>
  );
}

// ── 6개월 트렌드 차트 ───────────────────────────────────────
function TrendChart({ trend, isMobile }) {
  const maxVal = Math.max(...trend.map(d => Math.max(d.revenue, d.costs, 1)));
  const h = isMobile ? 80 : 110;
  const allZero = trend.every(d => d.revenue === 0 && d.costs === 0);
  if (allZero) {
    return (
      <div style={{ textAlign: 'center', color: '#bbb', padding: '24px 0', fontSize: 13 }}>
        손익관리에 데이터를 입력하면 그래프가 표시돼요
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: h + 24 }}>
        {trend.map((d, i) => {
          const rh = maxVal > 0 ? Math.round((d.revenue / maxVal) * h) : 0;
          const ch = maxVal > 0 ? Math.round((d.costs / maxVal) * h) : 0;
          const active = i === trend.length - 1;
          return (
            <div key={d.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: h }}>
                <div title={`매출: ${fmtWon(d.revenue)}`} style={{
                  width: isMobile ? 12 : 16, height: rh, background: active ? '#FF6F0F' : '#E4E4E7',
                  borderRadius: '3px 3px 0 0',
                }} />
                <div title={`비용: ${fmtWon(d.costs)}`} style={{
                  width: isMobile ? 12 : 16, height: ch, background: active ? '#B55041' : '#fde68a',
                  borderRadius: '3px 3px 0 0',
                }} />
              </div>
              <span style={{ fontSize: 10, color: active ? '#FF6F0F' : '#9ca3af', fontWeight: active ? 800 : 500 }}>
                {d.label}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 10, background: '#FF6F0F', borderRadius: 2 }} />
          <span style={{ fontSize: 11, color: '#71717A' }}>매출</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 10, background: '#B55041', borderRadius: 2 }} />
          <span style={{ fontSize: 11, color: '#71717A' }}>비용</span>
        </div>
      </div>
    </div>
  );
}
